import { useEffect, useState } from "preact/hooks";

import { Modal } from "./Modal";
import { CompaniesEditor } from "./CompaniesEditor";
import { ProfilesEditor } from "./ProfilesEditor";
import { fetchConfig, fetchEnv, saveConfig, saveEnv, setToken } from "../lib/api";
import type { Company, Config, Profile } from "../lib/types";
import { showToast } from "../store/appStore";

interface Props {
  open: boolean;
  onClose: () => void;
}

type Tab = "keywords" | "profiles" | "resume" | "companies" | "env" | "scheduler";

const TABS: { id: Tab; label: string; short: string; description: string }[] = [
  { id: "keywords", label: "Keywords", short: "Keys", description: "Include and exclude job keywords" },
  { id: "profiles", label: "Profiles", short: "Profiles", description: "Group keywords into target roles" },
  { id: "resume", label: "Resume Gap", short: "Resume", description: "Paste resume text for gap analysis" },
  { id: "companies", label: "Companies", short: "Cos", description: "Manage tracked companies and ATS" },
  { id: "env", label: "Email / Env", short: "Env", description: "API token and environment variables" },
  { id: "scheduler", label: "Scheduler", short: "Scan", description: "Auto-scan interval and archive rules" },
];

const DEFAULT_CONFIG: Config = {
  keywords: [],
  exclude: [],
  companies: [],
  watchlist: [],
  profiles: [],
  resume_text: "",
  tier1_companies: [],
  scan_interval_minutes: 60,
  archive_after_days: 30,
};

export function SettingsModal({ open, onClose }: Props) {
  const [tab, setTab] = useState<Tab | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<Config>(DEFAULT_CONFIG);
  const [env, setEnv] = useState<Record<string, string>>({});
  const [token, setTokenState] = useState("");
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Always land on the settings home when the modal opens.
    setTab(null);
    setShowToken(false);
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [c, e] = await Promise.all([fetchConfig(), fetchEnv()]);
        if (cancelled) return;
        setConfig(c || DEFAULT_CONFIG);
        setEnv(e || {});
        setTokenState(localStorage.getItem("dashboard_token") || "");
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  function update<K extends keyof Config>(key: K, value: Config[K]) {
    setConfig((c) => ({ ...c, [key]: value }));
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      // Basic inline validation (no alert()).
      if (!Array.isArray(config.companies)) throw new Error("Companies must be an array.");
      if (!Array.isArray(config.profiles)) throw new Error("Profiles must be an array.");
      for (const c of config.companies) {
        if (!c.name || !c.slug || !c.ats) throw new Error(`Company missing name/slug/ats: ${JSON.stringify(c)}`);
      }
      for (const p of config.profiles) {
        if (!p.name) throw new Error("Every profile must have a name.");
        if (!Array.isArray(p.keywords)) throw new Error(`Profile "${p.name}" keywords must be an array.`);
      }

      await saveConfig(config);
      const envToSave = Object.fromEntries(
        Object.entries(env).filter(([, value]) => value !== "****"),
      );
      if (token.trim()) {
        envToSave.DASHBOARD_TOKEN = token.trim();
      }
      await saveEnv(envToSave);
      setToken(token);
      showToast("Settings saved. Re-tagging jobs…");
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save settings");
    } finally {
      setSaving(false);
    }
  }

  function envText(): string {
    return Object.entries(env).map(([k, v]) => `${k}=${v}`).join("\n");
  }
  function setEnvText(text: string) {
    const next: Record<string, string> = {};
    for (const line of text.split("\n")) {
      const eq = line.indexOf("=");
      if (eq > 0) next[line.slice(0, eq).trim()] = line.slice(eq + 1).trim();
    }
    setEnv(next);
  }

  const activeTab = TABS.find((t) => t.id === tab) || null;
  const modalTitle = activeTab ? activeTab.label : "Settings";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle}
      maxWidth="840px"
      className={`settings-modal${tab ? " settings-modal--panel" : " settings-modal--home"}`}
      footer={
        <>
          <button class="platform-btn" onClick={onClose}>Cancel</button>
          <button class="btn-primary" onClick={save} disabled={loading || saving}>
            {saving ? <span class="spinner" /> : null}
            <span>Save Changes</span>
          </button>
        </>
      }
    >
      {loading && <div class="form-loading"><span class="spinner" /><span>Loading settings…</span></div>}
      {error && <div class="form-error">{error}</div>}
      {!loading && tab === null && (
        <div class="settings-home">
          <p class="settings-home-intro">Choose a section to configure your tracker.</p>
          <div class="settings-home-grid" role="list">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                class="settings-home-card"
                onClick={() => setTab(t.id)}
                role="listitem"
              >
                <span class="settings-home-card-title">{t.label}</span>
                <span class="settings-home-card-desc">{t.description}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {!loading && tab !== null && (
        <>
          <div class="settings-panel-nav">
            <button
              type="button"
              class="settings-back-btn"
              onClick={() => setTab(null)}
              aria-label="Back to settings home"
            >
              <span aria-hidden="true">←</span>
              <span>All settings</span>
            </button>
            <div class="settings-tabs" role="tablist" aria-label="Settings sections">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  class={`settings-tab${tab === t.id ? " active" : ""}`}
                  onClick={() => setTab(t.id)}
                  role="tab"
                  aria-selected={tab === t.id}
                  aria-label={t.label}
                >
                  <span class="settings-tab-full">{t.label}</span>
                  <span class="settings-tab-short">{t.short}</span>
                </button>
              ))}
            </div>
          </div>

          {tab === "keywords" && (
            <div class="settings-pane settings-pane--split">
              <div class="form-group">
                <label>Include Keywords (one per line)</label>
                <textarea
                  class="form-control textarea-control"
                  value={config.keywords.join("\n")}
                  onInput={(e) => update("keywords", e.currentTarget.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                />
              </div>
              <div class="form-group">
                <label>Exclude Keywords (one per line)</label>
                <textarea
                  class="form-control textarea-control"
                  value={config.exclude.join("\n")}
                  onInput={(e) => update("exclude", e.currentTarget.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                />
              </div>
            </div>
          )}

          {tab === "profiles" && (
            <div class="settings-pane">
              <p class="form-hint">Group keywords into target profiles (e.g. ML Infra, Applied ML).</p>
              <ProfilesEditor
                profiles={config.profiles}
                onChange={(profiles: Profile[]) => update("profiles", profiles)}
              />
            </div>
          )}

          {tab === "resume" && (
            <div class="settings-pane">
              <div class="form-group">
                <label>Resume / CV Text (plain text paste)</label>
                <p class="form-hint">Paste your plain text resume. We'll automatically identify missing noun phrases in JDs.</p>
                <textarea
                  class="form-control textarea-control"
                  style="min-height: 220px;"
                  value={config.resume_text}
                  onInput={(e) => update("resume_text", e.currentTarget.value)}
                />
              </div>
            </div>
          )}

          {tab === "companies" && (
            <div class="settings-pane">
              <p class="form-hint">
                Edit companies, domains, and ATS. Star a company for a +30 relevance score bonus.
                Prefer header &quot;Add Company&quot; to auto-resolve.
              </p>
              <CompaniesEditor
                companies={config.companies}
                tier1Companies={config.tier1_companies || []}
                onChange={(companies: Company[]) => update("companies", companies)}
                onTier1Change={(tier1_companies) => update("tier1_companies", tier1_companies)}
              />
            </div>
          )}

          {tab === "env" && (
            <div class="settings-pane">
              <div class="form-group">
                <label>Dashboard API Token</label>
                <p class="form-hint">Required when DASHBOARD_TOKEN is set on the server (all API routes).</p>
                <div class="token-input-row">
                  <input
                    type={showToken ? "text" : "password"}
                    class="form-control"
                    value={token}
                    onInput={(e) => setTokenState(e.currentTarget.value)}
                    placeholder="Dashboard password"
                  />
                  <button class="platform-btn" onClick={() => setShowToken((v) => !v)} aria-label={showToken ? "Hide token" : "Show token"}>
                    {showToken ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              <div class="form-group">
                <label>Environment Variables (.env format: KEY=VALUE)</label>
                <textarea
                  class="form-control textarea-control"
                  style="min-height: 220px; font-family: var(--font-mono);"
                  value={envText()}
                  onInput={(e) => setEnvText(e.currentTarget.value)}
                />
              </div>
            </div>
          )}

          {tab === "scheduler" && (
            <div class="settings-pane settings-pane--split">
              <div class="form-group">
                <label>Auto-Scan Interval (Minutes)</label>
                <p class="form-hint">Set to 0 to disable the background scheduler.</p>
                <input
                  type="number"
                  class="form-control"
                  min="0"
                  value={config.scan_interval_minutes || 0}
                  onInput={(e) => update("scan_interval_minutes", parseInt(e.currentTarget.value, 10) || 0)}
                />
              </div>
              <div class="form-group">
                <label>Auto-archive After (Days)</label>
                <p class="form-hint">Closed/expired postings archive after this many days.</p>
                <input
                  type="number"
                  class="form-control"
                  min="1"
                  value={config.archive_after_days || 30}
                  onInput={(e) => update("archive_after_days", parseInt(e.currentTarget.value, 10) || 30)}
                />
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
